import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isDefined, NcEstimatedMinimumPaymentT } from '@consumer-frontend/nc-utils';

import { NcButtonStyledAsLink } from '../NcButtonStyledAsLink';
import { NcMinimumPaymentEstimation } from '../NcMinimumPaymentEstimation';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/draw-request/minimum-payment-estimations.scss';

const COLLAPSED_PAYMENT_COUNT = 3;

export type NcMinimumPaymentEstimationsPropsT = {
  minimumPayments?: NcEstimatedMinimumPaymentT[];
};
export type NcMinimumPaymentEstimationsT = React.FC<NcMinimumPaymentEstimationsPropsT>;

export const NcMinimumPaymentEstimations: NcMinimumPaymentEstimationsT = ({ minimumPayments }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const wasExpandedFlag = useRef(false);
  const listId = useId();

  // Allows automatic scrolling back to the top of the table when you collapse, except on the first render
  useEffect(
    () => {
      if (isExpanded) {
        wasExpandedFlag.current = true;
      } else if (wasExpandedFlag.current) {
        rootRef.current?.scrollIntoView({ block: 'nearest' });
        wasExpandedFlag.current = false;
      }
    },
    [isExpanded],
  );

  const sortedPayments = useMemo(
    () => (isDefined(minimumPayments)
      ? [...minimumPayments].sort((a, b) => a.paymentDate.compare(b.paymentDate))
      : []
    ),
    [minimumPayments],
  );
  const visiblePayments = isExpanded ? sortedPayments : sortedPayments.slice(0, COLLAPSED_PAYMENT_COUNT);

  if (!visiblePayments.length) { return null; }

  return (
    <div className="nc-minimum-payment-estimations" ref={rootRef}>
      <div
        className="estimations-list"
        id={listId}
        role="list"
      >
        {visiblePayments.map((payment) => (
          <NcMinimumPaymentEstimation key={payment.paymentDate.toString()} {...payment} />
        ))}
      </div>

      {sortedPayments.length > COLLAPSED_PAYMENT_COUNT && (
        <NcButtonStyledAsLink
          aria-controls={listId}
          aria-expanded={isExpanded}
          className="estimations-toggle"
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </NcButtonStyledAsLink>
      )}
    </div>
  );
};
